import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArchiveRestore, Pencil } from "lucide-react";
import { Badge, Button, Card, FormField, Input } from "@/design-system/components/ui";
import { Select } from "@/components/ui/Select";
import { PageHeader } from "@/components/layout/PageHeader";
import { DataTable, type Column } from "@/components/layout/DataTable";
import { useAuth } from "@/hooks/useAuth";
import {
  DEAL_PERMISSIONS,
  STAGE_LABEL,
  type DealListItemDto,
  type DealListQuery,
  type DealPipelineStage,
} from "@/types/deals";
import { useDealsList } from "@/hooks/deals/useDeals";
import { dealLabel, formatCurrency } from "@/components/deals/dealFormat";
import { RestoreModal } from "@/components/deals/ArchiveRestoreModals";
import { DECLINE_REASONS, WITHDRAWAL_REASONS } from "@/api/dealLookups";
import { useDateFormatter } from "@/utils/datetime";

/**
 * Spec §5.4 — Archive list. Defaults to terminal-stage deals
 * (Declined / Withdrawn) with `IsArchived = true`. ADMIN sees the inline
 * Edit affordance; E1 / ADMIN see Restore.
 */
export function PipelineArchivePage() {
  const { hasPermission, hasRoleCode } = useAuth();
  const canRestore = hasPermission(DEAL_PERMISSIONS.Restore);
  const isAdmin = hasRoleCode("ADMIN");
  const { date } = useDateFormatter();

  const [search, setSearch] = useState("");
  const [stageFilter, setStageFilter] = useState<"All" | "Declined" | "Withdrawn">("All");
  const [reasonFilter, setReasonFilter] = useState("");
  const [restoreFor, setRestoreFor] = useState<DealListItemDto | null>(null);

  const query: DealListQuery = useMemo(() => {
    const stages: DealPipelineStage[] =
      stageFilter === "All" ? ["Declined", "Withdrawn"] : [stageFilter];
    return {
      pageSize: 200,
      stage: stages,
      includeArchived: true,
      q: search || undefined,
      declineReasonCode: stageFilter === "Withdrawn" ? undefined : (reasonFilter || undefined),
      withdrawalReasonCode: stageFilter === "Declined" ? undefined : (reasonFilter || undefined),
    };
  }, [search, stageFilter, reasonFilter]);

  const dealsQuery = useDealsList(query);
  const rows = (dealsQuery.data?.data ?? []).filter((d) => d.isArchived);

  const reasonOptions =
    stageFilter === "Withdrawn" ? WITHDRAWAL_REASONS : DECLINE_REASONS;

  const columns: Column<DealListItemDto>[] = [
    {
      key: "dealNumber",
      header: "Deal #",
      render: (d) => (
        <Link
          to={`/pipeline/${d.id}`}
          className="font-mono text-caption text-gold-shadow hover:text-gold-highlight"
        >
          {d.dealNumber}
        </Link>
      ),
    },
    {
      key: "businessName",
      header: "Business",
      render: (d) => (
        <Link
          to={`/pipeline/${d.id}`}
          className="font-semibold hover:text-gold-shadow"
        >
          {dealLabel(d)}
        </Link>
      ),
    },
    {
      key: "stage",
      header: "Outcome",
      render: (d) => (
        <Badge status={d.stage === "Declined" ? "error" : "archived"}>
          {STAGE_LABEL[d.stage]}
        </Badge>
      ),
    },
    {
      key: "purchasePrice",
      header: "Price",
      className: "text-right",
      render: (d) => (
        <span className="font-mono tabular-nums">{formatCurrency(d.purchasePrice)}</span>
      ),
    },
    {
      key: "createdAt",
      header: "Created",
      render: (d) => date(d.createdAt),
    },
    {
      key: "actions",
      header: "",
      className: "text-right",
      render: (d) => (
        <div className="flex items-center justify-end gap-space-2">
          {isAdmin && (
            <Link
              to={`/pipeline/${d.id}`}
              className="inline-flex items-center gap-space-1 text-caption text-gold-shadow hover:underline"
              title="Edit (admin)"
            >
              <Pencil size={12} aria-hidden /> Edit
            </Link>
          )}
          {canRestore && (
            <Button size="sm" variant="secondary" onClick={() => setRestoreFor(d)}>
              <ArchiveRestore size={12} aria-hidden /> Restore
            </Button>
          )}
        </div>
      ),
    },
  ];

  return (
    <div className="flex flex-col gap-space-4">
      <PageHeader
        eyebrow="Deal Flow"
        title="Archive"
        description="Declined and withdrawn deals after the 14-day grace period. Records retained for audit; restore returns a deal to the active pipeline."
      />

      <Card>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-space-3">
          <FormField label="Search">
            <Input
              placeholder="Deal #, business…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </FormField>
          <FormField label="Outcome">
            <Select
              value={stageFilter}
              onChange={(e) => {
                setStageFilter(e.target.value as typeof stageFilter);
                setReasonFilter("");
              }}
            >
              <option value="All">All</option>
              <option value="Declined">Declined</option>
              <option value="Withdrawn">Withdrawn</option>
            </Select>
          </FormField>
          <FormField label="Reason">
            <Select
              value={reasonFilter}
              onChange={(e) => setReasonFilter(e.target.value)}
              disabled={stageFilter === "All"}
            >
              <option value="">Any reason</option>
              {reasonOptions.map((r) => (
                <option key={r.code} value={r.code}>
                  {r.displayName}
                </option>
              ))}
            </Select>
          </FormField>
        </div>
      </Card>

      <DataTable
        columns={columns}
        rows={rows}
        loading={dealsQuery.isLoading}
        error={dealsQuery.isError ? "Could not load archive." : null}
        rowKey={(d) => d.id}
        emptyMessage={
          search || stageFilter !== "All" || reasonFilter
            ? "No archived deals match your filters."
            : "No archived deals yet."
        }
      />

      <RestoreModal
        open={!!restoreFor}
        deal={restoreFor}
        onClose={() => setRestoreFor(null)}
      />
    </div>
  );
}
